import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import {
  Folder,
  FileText,
  Upload,
  FolderPlus,
  Trash2,
  Download,
  ChevronRight,
  Home,
  Image as ImageIcon,
  Pencil,
  Tag,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { API_BASE_URL } from "@/lib/apiBase";
import { toastError, toastSuccess, getErrorMessage } from "@/lib/toast";
import {
  getEntitlementCode,
  toastEntitlementOrError,
} from "@/lib/entitlementToast";
import {
  convertCsvToSheets,
  convertDocToHtml,
  convertDocxToHtml,
  convertXlsxToSheets,
  isCsvMime,
  isDocMime,
  isDocxMime,
  isOfficePreviewMime,
  isSpreadsheetMime,
} from "@/lib/officePreview";
import {
  isAllowedTeamFileUpload,
  TEAM_FILE_ACCEPT,
  TEAM_FILE_TYPES_HINT,
  baseNameOf,
  extensionOf,
  preserveFileExtension,
} from "@/lib/teamFileUpload";
import { useConfirm } from "@/components/ConfirmDialogProvider";
import { cn } from "@/lib/utils";
import MarkdownBody from "./MarkdownBody";

const TEAM_FILE_DRAG_TYPE = "application/x-circa-team-file-node";

const SUGGESTED_FILE_TAGS = [
  "documentation",
  "policy",
  "procedures",
];

function isSuggestedTag(tag) {
  return SUGGESTED_FILE_TAGS.includes(tag);
}

function formatBytes(n) {
  const v = Number(n) || 0;
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} KB`;
  if (v < 1024 * 1024 * 1024) return `${(v / (1024 * 1024)).toFixed(1)} MB`;
  return `${(v / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function normalizeTagClient(raw) {
  return String(raw || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9._/-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 40);
}

function hasOsFiles(dt) {
  if (!dt) return false;
  if (dt.files && dt.files.length > 0) return true;
  return Array.from(dt.types || []).includes("Files");
}

function hasTeamFileDrag(dt) {
  return Array.from(dt?.types || []).includes(TEAM_FILE_DRAG_TYPE);
}

/**
 * Drive-like browser for team files (nested folders).
 */
const TeamFilesPanel = ({
  orgId,
  teamId,
  accessToken,
  currentUserId,
  isTeamAdmin,
  hideOrgUsageMeter = false,
  canManageSettings = false,
}) => {
  const confirm = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();
  const focusNodeParam = searchParams.get("node");
  const focusNodeId = focusNodeParam ? parseInt(focusNodeParam, 10) : null;
  const focusAppliedRef = useRef(false);
  const fileInputRef = useRef(null);
  const [parentId, setParentId] = useState(null);
  const [focusedNodeId, setFocusedNodeId] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [items, setItems] = useState([]);
  const [usage, setUsage] = useState({ used_bytes: 0, quota_bytes: 0 });
  const [canWrite, setCanWrite] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewText, setPreviewText] = useState(null);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewSheets, setPreviewSheets] = useState(null);
  const [previewSheetIndex, setPreviewSheetIndex] = useState(0);
  const [dropActive, setDropActive] = useState(false);
  const [folderDropId, setFolderDropId] = useState(null);
  const [draggingNodeId, setDraggingNodeId] = useState(null);
  const [uploadTags, setUploadTags] = useState([]);
  const [uploadTagDraft, setUploadTagDraft] = useState("");
  const [tagFilter, setTagFilter] = useState(null);
  const [teamTags, setTeamTags] = useState([]);
  const [tagEditTarget, setTagEditTarget] = useState(null);
  const [tagEditSelected, setTagEditSelected] = useState([]);
  const [tagEditDraft, setTagEditDraft] = useState("");
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkTagOpen, setBulkTagOpen] = useState(false);
  const [bulkTagSelected, setBulkTagSelected] = useState([]);
  const [bulkTagDraft, setBulkTagDraft] = useState("");
  const dragDepthRef = useRef(0);
  const previewUrlRef = useRef(null);

  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${accessToken}` }),
    [accessToken]
  );

  const load = useCallback(async () => {
    if (!accessToken || !orgId || !teamId) return;
    setLoading(true);
    setError(null);
    try {
      const params = { org_id: orgId };
      const useFocus =
        Number.isFinite(focusNodeId) &&
        !focusAppliedRef.current &&
        parentId == null;
      if (useFocus) {
        params.node = focusNodeId;
      } else if (parentId != null) {
        params.parent_id = parentId;
      }
      if (tagFilter) params.tag = tagFilter;
      const res = await axios.get(
        `${API_BASE_URL}/team/${teamId}/files`,
        { headers: authHeaders, params }
      );
      setItems(res.data?.items || []);
      setBreadcrumbs(res.data?.breadcrumbs || []);
      if (useFocus && res.data?.parent_id !== undefined) {
        setParentId(res.data.parent_id ?? null);
      }
      if (res.data?.focused_node_id != null) {
        setFocusedNodeId(res.data.focused_node_id);
        focusAppliedRef.current = true;
      }
      setUsage(
        res.data?.usage || { used_bytes: 0, quota_bytes: 5 * 1024 * 1024 * 1024 }
      );
      setTeamTags(
        Array.isArray(res.data?.team_tags) ? res.data.team_tags : []
      );
      setCanWrite(res.data?.access?.can_write !== false);
    } catch (err) {
      console.error(err);
      setError(
        err.response?.data?.message || "Failed to load team files"
      );
    } finally {
      setLoading(false);
    }
  }, [accessToken, orgId, teamId, parentId, tagFilter, authHeaders, focusNodeId]);

  useEffect(() => {
    focusAppliedRef.current = false;
    setFocusedNodeId(null);
    setParentId(null);
  }, [focusNodeId, teamId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [parentId, tagFilter]);

  useEffect(() => {
    if (!focusedNodeId) return;
    const t = window.setTimeout(() => {
      const el = document.getElementById(`file-node-${focusedNodeId}`);
      el?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, 80);
    const clear = window.setTimeout(() => setFocusedNodeId(null), 2500);
    return () => {
      window.clearTimeout(t);
      window.clearTimeout(clear);
    };
  }, [focusedNodeId, items]);

  useEffect(() => {
    return () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
    };
  }, []);

  const usagePct = usage.quota_bytes
    ? Math.min(100, (100 * usage.used_bytes) / usage.quota_bytes)
    : 0;

  const tagPickerOptions = useMemo(() => {
    const extras = new Set([
      ...teamTags,
      ...uploadTags,
      ...tagEditSelected,
      ...bulkTagSelected,
    ]);
    for (const t of SUGGESTED_FILE_TAGS) extras.delete(t);
    return [
      ...SUGGESTED_FILE_TAGS,
      ...[...extras].sort((a, b) => a.localeCompare(b)),
    ];
  }, [teamTags, uploadTags, tagEditSelected, bulkTagSelected]);

  const filterTagOptions = useMemo(() => {
    const extras = teamTags.filter((t) => !SUGGESTED_FILE_TAGS.includes(t));
    return [...SUGGESTED_FILE_TAGS, ...extras];
  }, [teamTags]);

  const fileItems = useMemo(
    () => items.filter((item) => item.kind === "file"),
    [items]
  );

  const allFilesSelected =
    fileItems.length > 0 && fileItems.every((f) => selectedIds.has(f.node_id));

  const canDeleteItem = (item) => {
    if (isTeamAdmin) return true;
    if (item.kind === "file") return item.uploaded_by === currentUserId;
    return item.uploaded_by === currentUserId;
  };

  const openFolder = (id) => {
    setFocusedNodeId(null);
    setSelectedIds(new Set());
    if (searchParams.get("node")) {
      const next = new URLSearchParams(searchParams);
      next.delete("node");
      setSearchParams(next, { replace: true });
    }
    setParentId(id);
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim() || busy) return;
    setBusy(true);
    try {
      await axios.post(
        `${API_BASE_URL}/team/${teamId}/files/folders`,
        {
          name: newFolderName.trim(),
          parent_id: parentId,
        },
        { headers: authHeaders, params: { org_id: orgId } }
      );
      toastSuccess("Folder created");
      setNewFolderOpen(false);
      setNewFolderName("");
      await load();
    } catch (err) {
      toastError(err, "Could not create folder");
    } finally {
      setBusy(false);
    }
  };

  const uploadOneFile = async (
    file,
    targetParentId = parentId,
    tagsOverride = null
  ) => {
    toastSuccess("Scanning for malware…");
    await new Promise((r) => setTimeout(r, 600));
    const form = new FormData();
    form.append("file", file);
    const tags =
      tagsOverride != null ? tagsOverride : uploadTags;
    if (tags.length) {
      form.append("tags", JSON.stringify(tags));
    }
    const params = { org_id: orgId };
    if (targetParentId != null) params.parent_id = targetParentId;
    await axios.post(
      `${API_BASE_URL}/team/${teamId}/files/upload`,
      form,
      {
        // Let the browser set multipart boundary — manual Content-Type drops fields like tags
        headers: authHeaders,
        params,
      }
    );
  };

  const toggleUploadTag = (tag) => {
    const t = normalizeTagClient(tag);
    if (!t) return;
    setUploadTags((prev) =>
      prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t].slice(0, 12)
    );
  };

  const addUploadTagDraft = () => {
    const t = normalizeTagClient(uploadTagDraft);
    if (!t) return;
    setUploadTags((prev) => {
      if (prev.includes(t)) return prev;
      return [...prev, t].slice(0, 12);
    });
    setUploadTagDraft("");
  };

  const removeCustomUploadTag = (tag) => {
    const t = normalizeTagClient(tag);
    if (!t || isSuggestedTag(t)) return;
    setUploadTags((prev) => prev.filter((x) => x !== t));
  };

  const collectFilesWithTag = async (tag) => {
    const t = normalizeTagClient(tag);
    if (!t) return [];
    const found = [];
    const queue = [null];
    const seenParents = new Set();
    while (queue.length) {
      const folderId = queue.shift();
      const key = folderId == null ? "root" : String(folderId);
      if (seenParents.has(key)) continue;
      seenParents.add(key);
      const params = { org_id: orgId };
      if (folderId != null) params.parent_id = folderId;
      const res = await axios.get(`${API_BASE_URL}/team/${teamId}/files`, {
        headers: authHeaders,
        params,
      });
      for (const item of res.data?.items || []) {
        if (item.kind === "folder") {
          queue.push(item.node_id);
        } else if (
          item.kind === "file" &&
          Array.isArray(item.tags) &&
          item.tags.includes(t)
        ) {
          found.push(item);
        }
      }
    }
    return found;
  };

  const stripTagFromTeam = async (tag) => {
    const t = normalizeTagClient(tag);
    if (!t || isSuggestedTag(t) || busy) return;
    const ok = await confirm({
      title: `Remove tag “${t}”?`,
      description: `This removes “${t}” from every file in this team that has it. The tag will disappear from the filter list once no files use it.`,
      confirmLabel: "Remove tag",
      variant: "destructive",
    });
    if (!ok) return;
    setBusy(true);
    try {
      const files = await collectFilesWithTag(t);
      let updated = 0;
      let failed = 0;
      for (const file of files) {
        const nextTags = (file.tags || []).filter((x) => x !== t);
        try {
          await axios.patch(
            `${API_BASE_URL}/team/${teamId}/files/${file.node_id}`,
            { tags: nextTags },
            { headers: authHeaders, params: { org_id: orgId } }
          );
          updated += 1;
        } catch {
          failed += 1;
        }
      }
      if (tagFilter === t) setTagFilter(null);
      setUploadTags((prev) => prev.filter((x) => x !== t));
      await load();
      if (failed && !updated) {
        toastError("Could not remove tag from files");
      } else if (failed) {
        toastError(
          `Removed from ${updated} file${updated === 1 ? "" : "s"}; ${failed} failed`
        );
      } else {
        toastSuccess(
          updated === 0
            ? "Tag removed"
            : `Removed tag from ${updated} file${updated === 1 ? "" : "s"}`
        );
      }
    } catch (err) {
      toastError(err, "Could not remove tag");
    } finally {
      setBusy(false);
    }
  };

  const toggleFileSelected = (nodeId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });
  };

  const toggleSelectAllFiles = () => {
    setSelectedIds((prev) => {
      if (fileItems.length === 0) return prev;
      const allOn = fileItems.every((f) => prev.has(f.node_id));
      if (allOn) return new Set();
      return new Set(fileItems.map((f) => f.node_id));
    });
  };

  const openBulkTagDialog = () => {
    setBulkTagSelected([]);
    setBulkTagDraft("");
    setBulkTagOpen(true);
  };

  const toggleBulkTag = (tag) => {
    const t = normalizeTagClient(tag);
    if (!t) return;
    setBulkTagSelected((prev) =>
      prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t].slice(0, 12)
    );
  };

  const addBulkTagDraft = () => {
    const t = normalizeTagClient(bulkTagDraft);
    if (!t) return;
    setBulkTagSelected((prev) => {
      if (prev.includes(t)) return prev;
      return [...prev, t].slice(0, 12);
    });
    setBulkTagDraft("");
  };

  const handleBulkTagSave = async () => {
    let tagsToMerge = bulkTagSelected;
    const draft = normalizeTagClient(bulkTagDraft);
    if (draft && !tagsToMerge.includes(draft)) {
      tagsToMerge = [...tagsToMerge, draft].slice(0, 12);
      setBulkTagSelected(tagsToMerge);
      setBulkTagDraft("");
    }
    if (!tagsToMerge.length || selectedIds.size === 0 || busy) return;
    setBusy(true);
    try {
      let updated = 0;
      let failed = 0;
      for (const id of selectedIds) {
        const item = items.find((i) => i.node_id === id && i.kind === "file");
        if (!item) continue;
        const merged = [
          ...new Set([...(item.tags || []), ...tagsToMerge]),
        ].slice(0, 12);
        try {
          await axios.patch(
            `${API_BASE_URL}/team/${teamId}/files/${id}`,
            { tags: merged },
            { headers: authHeaders, params: { org_id: orgId } }
          );
          updated += 1;
        } catch {
          failed += 1;
        }
      }
      setBulkTagOpen(false);
      setSelectedIds(new Set());
      await load();
      if (failed && !updated) {
        toastError("Could not update tags");
      } else if (failed) {
        toastError(
          `Tagged ${updated} file${updated === 1 ? "" : "s"}; ${failed} failed`
        );
      } else {
        toastSuccess(
          `Tagged ${updated} file${updated === 1 ? "" : "s"}`
        );
      }
    } catch (err) {
      toastError(err, "Could not update tags");
    } finally {
      setBusy(false);
    }
  };

  const handleBulkDelete = async () => {
    const toDelete = fileItems.filter(
      (item) =>
        selectedIds.has(item.node_id) &&
        !item.is_system &&
        canDeleteItem(item)
    );
    if (!toDelete.length) {
      toastError("No selected files you can delete");
      return;
    }
    const skipped = selectedIds.size - toDelete.length;
    const ok = await confirm({
      title:
        toDelete.length === 1
          ? "Delete file?"
          : `Delete ${toDelete.length} files?`,
      description:
        skipped > 0
          ? `Delete ${toDelete.length} file${toDelete.length === 1 ? "" : "s"}? ${skipped} selected item${skipped === 1 ? " was" : "s were"} skipped (no permission). This cannot be undone.`
          : `Delete ${toDelete.length} file${toDelete.length === 1 ? "" : "s"}? This cannot be undone.`,
      confirmLabel: "Delete",
      variant: "destructive",
    });
    if (!ok) return;
    setBusy(true);
    try {
      let deleted = 0;
      let failed = 0;
      for (const item of toDelete) {
        try {
          await axios.delete(
            `${API_BASE_URL}/team/${teamId}/files/${item.node_id}`,
            { headers: authHeaders, params: { org_id: orgId } }
          );
          deleted += 1;
        } catch {
          failed += 1;
        }
      }
      setSelectedIds(new Set());
      await load();
      if (failed && !deleted) {
        toastError("Could not delete files");
      } else if (failed) {
        toastError(
          `Deleted ${deleted}; ${failed} failed`
        );
      } else {
        toastSuccess(
          deleted === 1 ? "Deleted" : `Deleted ${deleted} files`
        );
      }
    } catch (err) {
      toastError(err, "Could not delete");
    } finally {
      setBusy(false);
    }
  };

  const openTagEditor = (item) => {
    setTagEditTarget(item);
    setTagEditSelected([...(item.tags || [])]);
    setTagEditDraft("");
  };

  const toggleTagEdit = (tag) => {
    const t = normalizeTagClient(tag);
    if (!t) return;
    setTagEditSelected((prev) =>
      prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t].slice(0, 12)
    );
  };

  const addTagEditDraft = () => {
    const t = normalizeTagClient(tagEditDraft);
    if (!t) return false;
    setTagEditSelected((prev) => {
      if (prev.includes(t)) return prev;
      return [...prev, t].slice(0, 12);
    });
    setTagEditDraft("");
    return true;
  };

  const handleSaveTags = async () => {
    if (!tagEditTarget || busy) return;
    // Include any tag still sitting in the draft input
    let tagsToSave = tagEditSelected;
    const draft = normalizeTagClient(tagEditDraft);
    if (draft && !tagsToSave.includes(draft)) {
      tagsToSave = [...tagsToSave, draft].slice(0, 12);
      setTagEditSelected(tagsToSave);
      setTagEditDraft("");
    }
    setBusy(true);
    try {
      await axios.patch(
        `${API_BASE_URL}/team/${teamId}/files/${tagEditTarget.node_id}`,
        { tags: tagsToSave },
        { headers: authHeaders, params: { org_id: orgId } }
      );
      toastSuccess("Tags updated");
      setTagEditTarget(null);
      await load();
    } catch (err) {
      toastError(err, "Could not update tags");
    } finally {
      setBusy(false);
    }
  };

  // Include any pending draft in upload tags as well
  const uploadFiles = async (fileList, targetParentId = parentId) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length || busy) return;
    const draft = normalizeTagClient(uploadTagDraft);
    if (draft && !uploadTags.includes(draft)) {
      setUploadTags((prev) => [...prev, draft].slice(0, 12));
      setUploadTagDraft("");
    }
    const tagsForUpload =
      draft && !uploadTags.includes(draft)
        ? [...uploadTags, draft].slice(0, 12)
        : uploadTags;

    const allowed = [];
    const skipped = [];
    for (const file of files) {
      if (isAllowedTeamFileUpload(file.name, file.type)) {
        allowed.push(file);
      } else {
        skipped.push(file.name);
      }
    }

    if (skipped.length) {
      const names =
        skipped.length <= 5
          ? skipped.map((n) => `"${n}"`).join(", ")
          : `${skipped
              .slice(0, 4)
              .map((n) => `"${n}"`)
              .join(", ")} and ${skipped.length - 4} more`;
      toastError(
        skipped.length === 1
          ? `${names} isn’t a supported type. Allowed: ${TEAM_FILE_TYPES_HINT}.`
          : `${skipped.length} files aren’t supported (${names}). Allowed: ${TEAM_FILE_TYPES_HINT}.`
      );
    }
    if (!allowed.length) return;

    setBusy(true);
    const uploaded = [];
    const failed = [];
    try {
      for (const file of allowed) {
        try {
          await uploadOneFile(file, targetParentId, tagsForUpload);
          uploaded.push(file.name);
        } catch (err) {
          failed.push({ name: file.name, err });
        }
      }
      if (uploaded.length) {
        toastSuccess(
          uploaded.length === 1
            ? "Clean — file uploaded"
            : `Clean — ${uploaded.length} files uploaded`
        );
        await load();
      }
      if (failed.length) {
        const entitlementFail = failed.find((f) => getEntitlementCode(f.err));
        if (entitlementFail) {
          toastEntitlementOrError(
            entitlementFail.err,
            "Upload failed",
            { canManageSettings, orgId }
          );
        } else {
          const detail =
            failed.length === 1
              ? `"${failed[0].name}": ${getErrorMessage(failed[0].err, "Upload failed")}`
              : failed
                  .map(
                    (f) =>
                      `"${f.name}" (${getErrorMessage(f.err, "failed")})`
                  )
                  .join("; ");
          toastError(
            failed.length === 1
              ? `Couldn’t upload ${detail}`
              : `Couldn’t upload ${failed.length} files: ${detail}`
          );
        }
      }
    } finally {
      setBusy(false);
    }
  };

  const handleUploadInput = async (e) => {
    // Copy before clearing — FileList is live; resetting value empties it in
    // many browsers, so click-to-browse would "select" but upload nothing.
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    await uploadFiles(files, parentId);
  };

  const moveNode = async (nodeId, newParentId) => {
    if (busy) return;
    setBusy(true);
    try {
      await axios.patch(
        `${API_BASE_URL}/team/${teamId}/files/${nodeId}`,
        { parent_id: newParentId },
        { headers: authHeaders, params: { org_id: orgId } }
      );
      toastSuccess("Moved");
      await load();
    } catch (err) {
      toastError(err, "Could not move");
    } finally {
      setBusy(false);
      setFolderDropId(null);
      setDraggingNodeId(null);
    }
  };

  const handleDelete = async (item) => {
    const ok = await confirm({
      title: item.kind === "folder" ? "Delete folder?" : "Delete file?",
      description:
        item.kind === "folder"
          ? isTeamAdmin
            ? `Delete “${item.name}” and everything inside it?`
            : `Delete empty folder “${item.name}”?`
          : `Delete “${item.name}”? This cannot be undone.`,
      confirmLabel: "Delete",
      variant: "destructive",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await axios.delete(
        `${API_BASE_URL}/team/${teamId}/files/${item.node_id}`,
        { headers: authHeaders, params: { org_id: orgId } }
      );
      toastSuccess("Deleted");
      await load();
    } catch (err) {
      toastError(err, "Could not delete");
    } finally {
      setBusy(false);
    }
  };

  const handleRename = async () => {
    if (!renameTarget || !renameValue.trim() || busy) return;
    const nextName =
      renameTarget.kind === "file"
        ? preserveFileExtension(renameTarget.name, renameValue.trim())
        : renameValue.trim();
    setBusy(true);
    try {
      await axios.patch(
        `${API_BASE_URL}/team/${teamId}/files/${renameTarget.node_id}`,
        { name: nextName },
        { headers: authHeaders, params: { org_id: orgId } }
      );
      toastSuccess("Renamed");
      setRenameTarget(null);
      await load();
    } catch (err) {
      toastError(err, "Could not rename");
    } finally {
      setBusy(false);
    }
  };

  const renameExtension =
    renameTarget?.kind === "file" ? extensionOf(renameTarget.name) : "";

  const contentUrl = (nodeId, disposition) =>
    `${API_BASE_URL}/team/${teamId}/files/${nodeId}/content?org_id=${encodeURIComponent(
      orgId
    )}&disposition=${disposition}`;

  const handleDownload = async (item) => {
    try {
      const res = await axios.get(contentUrl(item.node_id, "attachment"), {
        headers: authHeaders,
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = item.name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toastError(err, "Download failed");
    }
  };

  const clearPreviewMedia = () => {
    setPreviewText(null);
    setPreviewHtml(null);
    setPreviewSheets(null);
    setPreviewSheetIndex(0);
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setPreviewUrl(null);
  };

  const handlePreview = async (item) => {
    if (!item.previewable) {
      await handleDownload(item);
      return;
    }
    setPreview(item);
    clearPreviewMedia();
    try {
      const res = await axios.get(contentUrl(item.node_id, "inline"), {
        headers: authHeaders,
        responseType: "blob",
      });
      const mime = item.mime_type || "";
      const isText =
        (mime.startsWith("text/") ||
          mime === "text/csv" ||
          mime === "application/csv") &&
        !isCsvMime(mime);
      if (isOfficePreviewMime(mime)) {
        try {
          const arrayBuffer = await res.data.arrayBuffer();
          if (isDocMime(mime)) {
            setPreviewHtml(await convertDocToHtml(arrayBuffer));
          } else if (isDocxMime(mime)) {
            setPreviewHtml(await convertDocxToHtml(arrayBuffer));
          } else if (isSpreadsheetMime(mime)) {
            const sheets = await convertXlsxToSheets(arrayBuffer);
            setPreviewSheets(sheets);
            setPreviewSheetIndex(0);
          }
        } catch (convertErr) {
          toastError(convertErr, "Could not preview file — downloading instead");
          setPreview(null);
          clearPreviewMedia();
          await handleDownload(item);
        }
      } else if (isCsvMime(mime) || (item.name || "").toLowerCase().endsWith(".csv")) {
        try {
          const text = await res.data.text();
          try {
            const sheets = convertCsvToSheets(text);
            setPreviewSheets(sheets);
            setPreviewSheetIndex(0);
          } catch (convertErr) {
            toastError(convertErr, "Could not parse CSV — showing raw text");
            setPreviewText(text);
          }
        } catch (err) {
          toastError(err, "Could not preview CSV — downloading instead");
          setPreview(null);
          clearPreviewMedia();
          await handleDownload(item);
        }
      } else if (isText) {
        const text = await res.data.text();
        setPreviewText(text);
      } else {
        const url = URL.createObjectURL(res.data);
        previewUrlRef.current = url;
        setPreviewUrl(url);
      }
    } catch (err) {
      toastError(err, "Preview failed");
      setPreview(null);
      clearPreviewMedia();
    }
  };

  const previewIsMarkdown = (() => {
    if (!preview) return false;
    const mime = (preview.mime_type || "").split(";")[0].trim().toLowerCase();
    const name = (preview.name || "").toLowerCase();
    return mime === "text/markdown" || name.endsWith(".md");
  })();

  const iconFor = (item) => {
    if (item.kind === "folder") {
      return <Folder className="h-4 w-4 text-primary shrink-0" />;
    }
    if ((item.mime_type || "").startsWith("image/")) {
      return <ImageIcon className="h-4 w-4 text-muted-foreground shrink-0" />;
    }
    return <FileText className="h-4 w-4 text-muted-foreground shrink-0" />;
  };

  const onDropZoneDragEnter = (e) => {
    if (!hasOsFiles(e.dataTransfer) || hasTeamFileDrag(e.dataTransfer)) return;
    e.preventDefault();
    dragDepthRef.current += 1;
    setDropActive(true);
  };

  const onDropZoneDragLeave = (e) => {
    if (!hasOsFiles(e.dataTransfer)) return;
    e.preventDefault();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setDropActive(false);
  };

  const onDropZoneDragOver = (e) => {
    if (!hasOsFiles(e.dataTransfer) || hasTeamFileDrag(e.dataTransfer)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };

  const onDropZoneDrop = async (e) => {
    e.preventDefault();
    dragDepthRef.current = 0;
    setDropActive(false);
    if (hasTeamFileDrag(e.dataTransfer)) return;
    const files = e.dataTransfer.files;
    if (files?.length) await uploadFiles(files, parentId);
  };

  const onFolderDragOver = (e, folderId) => {
    if (hasTeamFileDrag(e.dataTransfer)) {
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = "move";
      setFolderDropId(folderId);
      return;
    }
    if (hasOsFiles(e.dataTransfer)) {
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = "copy";
      setFolderDropId(folderId);
    }
  };

  const onFolderDrop = async (e, folderId) => {
    e.preventDefault();
    e.stopPropagation();
    setFolderDropId(null);
    const nodeRaw = e.dataTransfer.getData(TEAM_FILE_DRAG_TYPE);
    if (nodeRaw) {
      const nodeId = parseInt(nodeRaw, 10);
      if (!Number.isNaN(nodeId) && nodeId !== folderId) {
        await moveNode(nodeId, folderId);
      }
      return;
    }
    if (e.dataTransfer.files?.length) {
      await uploadFiles(e.dataTransfer.files, folderId);
    }
  };

  return (
    <div className="space-y-4 md:pb-24">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-1">
          <div className="text-xs text-muted-foreground">
            {!hideOrgUsageMeter && (
              <>
                Org storage {formatBytes(usage.used_bytes)} /{" "}
                {formatBytes(usage.quota_bytes)}
                <div className="mt-1 h-1.5 w-full max-w-xs rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full bg-primary transition-all"
                    style={{ width: `${usagePct}%` }}
                  />
                </div>
              </>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={busy || !canWrite || parentId == null}
            title={
              parentId == null
                ? "Open Internal or Organization to create a folder"
                : undefined
            }
            onClick={() => setNewFolderOpen(true)}
          >
            <FolderPlus className="h-4 w-4 mr-1" />
            New folder
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={busy || !canWrite || parentId == null}
            title={
              parentId == null
                ? "Open Internal or Organization to upload"
                : undefined
            }
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="h-4 w-4 mr-1" />
            Upload
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            multiple
            accept={TEAM_FILE_ACCEPT}
            onChange={handleUploadInput}
          />
        </div>
      </div>

      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}

      <div
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            fileInputRef.current?.click();
          }
        }}
        onClick={() =>
          !busy && canWrite && parentId != null && fileInputRef.current?.click()
        }
        onDragEnter={canWrite && parentId != null ? onDropZoneDragEnter : undefined}
        onDragLeave={canWrite && parentId != null ? onDropZoneDragLeave : undefined}
        onDragOver={canWrite && parentId != null ? onDropZoneDragOver : undefined}
        onDrop={canWrite && parentId != null ? onDropZoneDrop : undefined}
        className={cn(
          "rounded-lg border-2 border-dashed px-4 py-8 text-center transition-colors",
          canWrite && parentId != null
            ? "cursor-pointer"
            : "cursor-default opacity-70",
          dropActive
            ? "border-primary bg-primary/5"
            : "border-border bg-muted/20 hover:border-primary/40 hover:bg-muted/40",
          busy && "pointer-events-none opacity-60"
        )}
      >
        <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
        <p className="text-sm font-medium">
          {!canWrite
            ? "View only — team members can upload"
            : parentId == null
              ? "Open Internal or Organization to upload"
              : dropActive
                ? "Drop to upload"
                : "Drag files here to upload"}
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          {parentId == null
            ? "Internal is members-only; Organization is visible to anyone who can view this team"
            : "or click to browse — files land in the current folder"}
        </p>
      </div>

      {canWrite && (
        <div className="space-y-2 pb-4 mb-1 border-b border-border">
          <p className="text-xs font-medium text-muted-foreground">
            Tags for new uploads
            {uploadTags.length > 0 && (
              <span className="font-normal">
                {" "}
                ({uploadTags.length} selected)
              </span>
            )}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {tagPickerOptions.map((tag) => {
              const on = uploadTags.includes(tag);
              const custom = !isSuggestedTag(tag);
              const showRemove =
                custom && (on || !teamTags.includes(tag));
              return (
                <span
                  key={tag}
                  className={cn(
                    "inline-flex items-center rounded-md border text-xs transition-colors",
                    on
                      ? "border-primary bg-primary/10 text-foreground"
                      : "border-border text-muted-foreground"
                  )}
                >
                  <button
                    type="button"
                    onClick={() => toggleUploadTag(tag)}
                    className={cn(
                      "px-2 py-0.5 hover:border-primary/40",
                      showRemove && "pr-1"
                    )}
                  >
                    {tag}
                  </button>
                  {showRemove && (
                    <button
                      type="button"
                      aria-label={`Remove tag ${tag}`}
                      className="px-1 py-0.5 text-muted-foreground hover:text-foreground"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        removeCustomUploadTag(tag);
                      }}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </span>
              );
            })}
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <Input
              value={uploadTagDraft}
              onChange={(e) => setUploadTagDraft(e.target.value)}
              placeholder="Add custom tag…"
              className="h-7 max-w-[14rem] text-xs"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  e.stopPropagation();
                  addUploadTagDraft();
                }
              }}
            />
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-xs"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                addUploadTagDraft();
              }}
              disabled={!normalizeTagClient(uploadTagDraft)}
            >
              Add tag
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-2 pt-1">
        <p className="text-xs font-medium text-muted-foreground">
          Filter by tags
        </p>
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            type="button"
            onClick={() => setTagFilter(null)}
            className={cn(
              "rounded-md border px-2 py-0.5 text-xs",
              !tagFilter
                ? "border-primary bg-primary/10"
                : "border-border text-muted-foreground"
            )}
          >
            All
          </button>
          {filterTagOptions.map((tag) => {
            const custom = !isSuggestedTag(tag);
            const active = tagFilter === tag;
            return (
              <span
                key={`filter-${tag}`}
                className={cn(
                  "inline-flex items-center rounded-md border text-xs",
                  active
                    ? "border-primary bg-primary/10"
                    : "border-border text-muted-foreground"
                )}
              >
                <button
                  type="button"
                  onClick={() => setTagFilter(active ? null : tag)}
                  className={cn("px-2 py-0.5", custom && canWrite && "pr-1")}
                >
                  {tag}
                </button>
                {custom && canWrite && (
                  <button
                    type="button"
                    aria-label={`Remove tag ${tag} from team files`}
                    className="px-1 py-0.5 text-muted-foreground hover:text-destructive"
                    disabled={busy}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      stripTagFromTeam(tag);
                    }}
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </span>
            );
          })}
        </div>
      </div>

      <nav className="flex flex-wrap items-center gap-1 text-sm text-muted-foreground">
        <button
          type="button"
          className="inline-flex items-center gap-1 hover:text-foreground"
          onClick={() => openFolder(null)}
        >
          <Home className="h-3.5 w-3.5" />
          Files
        </button>
        {breadcrumbs.map((c) => (
          <React.Fragment key={c.node_id}>
            <ChevronRight className="h-3.5 w-3.5" />
            <button
              type="button"
              className="hover:text-foreground truncate max-w-[10rem]"
              onClick={() => openFolder(c.node_id)}
            >
              {c.name}
            </button>
          </React.Fragment>
        ))}
      </nav>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading files…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center">
          This folder is empty. Drop files above or create a folder.
        </p>
      ) : (
        <div className="space-y-2">
          {canWrite && fileItems.length > 0 && (
            <div className="flex items-center gap-2 px-1">
              <label className="inline-flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 rounded border-border"
                  checked={allFilesSelected}
                  onChange={toggleSelectAllFiles}
                  disabled={busy}
                />
                Select all in this folder
              </label>
            </div>
          )}
          <ul className="divide-y divide-border rounded-lg border border-border">
            {items.map((item) => {
              const isFolderDropTarget =
                item.kind === "folder" && folderDropId === item.node_id;
              const isSelected = selectedIds.has(item.node_id);
              return (
                <li
                  key={item.node_id}
                  id={`file-node-${item.node_id}`}
                  draggable={item.kind === "file" && canWrite}
                  onDragStart={(e) => {
                    if (item.kind !== "file" || !canWrite) return;
                    e.dataTransfer.setData(
                      TEAM_FILE_DRAG_TYPE,
                      String(item.node_id)
                    );
                    e.dataTransfer.effectAllowed = "move";
                    setDraggingNodeId(item.node_id);
                  }}
                  onDragEnd={() => {
                    setDraggingNodeId(null);
                    setFolderDropId(null);
                  }}
                  onDragOver={
                    item.kind === "folder"
                      ? (e) => onFolderDragOver(e, item.node_id)
                      : undefined
                  }
                  onDragLeave={
                    item.kind === "folder"
                      ? () => {
                          setFolderDropId((cur) =>
                            cur === item.node_id ? null : cur
                          );
                        }
                      : undefined
                  }
                  onDrop={
                    item.kind === "folder"
                      ? (e) => onFolderDrop(e, item.node_id)
                      : undefined
                  }
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 hover:bg-muted/40",
                    item.kind === "file" && "cursor-grab active:cursor-grabbing",
                    draggingNodeId === item.node_id && "opacity-50",
                    isFolderDropTarget &&
                      "bg-primary/10 ring-1 ring-inset ring-primary",
                    focusedNodeId === item.node_id &&
                      "ring-2 ring-primary/40 bg-primary/5",
                    isSelected && "bg-primary/5"
                  )}
                >
                  {canWrite && item.kind === "file" && (
                    <input
                      type="checkbox"
                      className="h-3.5 w-3.5 shrink-0 rounded border-border"
                      checked={isSelected}
                      disabled={busy}
                      aria-label={`Select ${item.name}`}
                      onChange={() => toggleFileSelected(item.node_id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}
                  <button
                    type="button"
                    className="flex min-w-0 flex-1 items-center gap-2 text-left"
                    onClick={() =>
                      item.kind === "folder"
                        ? openFolder(item.node_id)
                        : handlePreview(item)
                    }
                  >
                    {iconFor(item)}
                    <span className="min-w-0 flex-1">
                      <span className="truncate text-sm font-medium block">
                        {item.name}
                      </span>
                      {item.kind === "folder" && (
                        <span className="mt-0.5 flex flex-wrap gap-1">
                          <Badge
                            variant="outline"
                            className="text-[10px] px-1.5 py-0 font-normal"
                          >
                            {item.visibility === "organization"
                              ? "Organization-wide"
                              : "Team only"}
                          </Badge>
                        </span>
                      )}
                      {item.kind === "file" &&
                        Array.isArray(item.tags) &&
                        item.tags.length > 0 && (
                          <span className="mt-0.5 flex flex-wrap gap-1">
                            {item.tags.map((tag) => (
                              <Badge
                                key={tag}
                                variant="outline"
                                className="text-[10px] px-1.5 py-0 font-normal"
                              >
                                {tag}
                              </Badge>
                            ))}
                          </span>
                        )}
                    </span>
                    {item.kind === "file" && (
                      <span className="text-xs text-muted-foreground shrink-0">
                        {formatBytes(item.size_bytes)}
                      </span>
                    )}
                    {isFolderDropTarget && (
                      <span className="text-xs text-primary shrink-0">
                        Drop to move here
                      </span>
                    )}
                  </button>
                  <div className="flex items-center gap-1 shrink-0">
                    {item.kind === "file" && (
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        title="Tags"
                        onClick={() => openTagEditor(item)}
                      >
                        <Tag className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {item.kind === "file" && (
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        title="Download"
                        onClick={() => handleDownload(item)}
                      >
                        <Download className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {canWrite && !item.is_system && (
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        title="Rename"
                        onClick={() => {
                          setRenameTarget(item);
                          setRenameValue(
                            item.kind === "file"
                              ? baseNameOf(item.name)
                              : item.name
                          );
                        }}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {canWrite && !item.is_system && canDeleteItem(item) && (
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-destructive"
                        title="Delete"
                        disabled={busy}
                        onClick={() => handleDelete(item)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
          {selectedIds.size > 0 && canWrite && (
            <div className="sticky bottom-2 z-10 flex flex-wrap items-center gap-2 rounded-lg border border-border bg-background/95 px-3 py-2 shadow-sm backdrop-blur supports-[backdrop-filter]:bg-background/80">
              <span className="text-xs font-medium">
                {selectedIds.size} selected
              </span>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-xs"
                onClick={() => setSelectedIds(new Set())}
                disabled={busy}
              >
                Clear
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-7 px-2 text-xs"
                onClick={openBulkTagDialog}
                disabled={busy}
              >
                <Tag className="h-3.5 w-3.5 mr-1" />
                Tag…
              </Button>
              <Button
                type="button"
                size="sm"
                variant="destructive"
                className="h-7 px-2 text-xs"
                onClick={handleBulkDelete}
                disabled={busy}
              >
                <Trash2 className="h-3.5 w-3.5 mr-1" />
                Delete
              </Button>
            </div>
          )}
        </div>
      )}

      <Dialog open={newFolderOpen} onOpenChange={setNewFolderOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New folder</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Label htmlFor="folder-name">Name</Label>
            <Input
              id="folder-name"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="Folder name"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleCreateFolder();
                }
              }}
            />
            <Button
              type="button"
              disabled={busy || !newFolderName.trim()}
              onClick={handleCreateFolder}
            >
              Create
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!renameTarget}
        onOpenChange={(o) => !o && setRenameTarget(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Input
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleRename();
                  }
                }}
                aria-label={
                  renameExtension ? "File name without extension" : "Name"
                }
              />
              {renameExtension ? (
                <span className="text-sm text-muted-foreground shrink-0 font-mono">
                  .{renameExtension}
                </span>
              ) : null}
            </div>
            {renameExtension ? (
              <p className="text-xs text-muted-foreground">
                File type (.{renameExtension}) can’t be changed when renaming.
              </p>
            ) : null}
            <Button
              type="button"
              disabled={busy || !renameValue.trim()}
              onClick={handleRename}
            >
              Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!tagEditTarget}
        onOpenChange={(o) => !o && setTagEditTarget(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Tags{tagEditTarget ? ` — ${tagEditTarget.name}` : ""}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Anyone with access to this file can add or remove tags.
            </p>
            <div className="flex flex-wrap gap-1.5">
              {tagPickerOptions.map((tag) => {
                const on = tagEditSelected.includes(tag);
                const custom = !isSuggestedTag(tag);
                return (
                  <span
                    key={tag}
                    className={cn(
                      "inline-flex items-center rounded-md border text-xs transition-colors",
                      on
                        ? "border-primary bg-primary/10 text-foreground"
                        : "border-border text-muted-foreground"
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => toggleTagEdit(tag)}
                      className={cn(
                        "px-2 py-0.5 hover:border-primary/40",
                        custom && on && "pr-1"
                      )}
                    >
                      {tag}
                    </button>
                    {custom && on && (
                      <button
                        type="button"
                        aria-label={`Remove tag ${tag}`}
                        className="px-1 py-0.5 text-muted-foreground hover:text-foreground"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleTagEdit(tag);
                        }}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </span>
                );
              })}
            </div>
            <div className="flex flex-wrap gap-2 items-center">
              <Input
                value={tagEditDraft}
                onChange={(e) => setTagEditDraft(e.target.value)}
                placeholder="Add custom tag…"
                className="h-7 max-w-[14rem] text-xs"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    e.stopPropagation();
                    addTagEditDraft();
                  }
                }}
              />
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-xs"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  addTagEditDraft();
                }}
                disabled={!normalizeTagClient(tagEditDraft)}
              >
                Add tag
              </Button>
            </div>
            <Button type="button" disabled={busy} onClick={handleSaveTags}>
              Save tags
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={bulkTagOpen}
        onOpenChange={(o) => {
          if (!o) setBulkTagOpen(false);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Tag {selectedIds.size} file{selectedIds.size === 1 ? "" : "s"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Chosen tags are merged onto each selected file (existing tags are
              kept).
            </p>
            <div className="flex flex-wrap gap-1.5">
              {tagPickerOptions.map((tag) => {
                const on = bulkTagSelected.includes(tag);
                return (
                  <button
                    key={`bulk-${tag}`}
                    type="button"
                    onClick={() => toggleBulkTag(tag)}
                    className={cn(
                      "rounded-md border px-2 py-0.5 text-xs transition-colors",
                      on
                        ? "border-primary bg-primary/10 text-foreground"
                        : "border-border text-muted-foreground hover:border-primary/40"
                    )}
                  >
                    {tag}
                  </button>
                );
              })}
            </div>
            <div className="flex flex-wrap gap-2 items-center">
              <Input
                value={bulkTagDraft}
                onChange={(e) => setBulkTagDraft(e.target.value)}
                placeholder="Add custom tag…"
                className="h-7 max-w-[14rem] text-xs"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    e.stopPropagation();
                    addBulkTagDraft();
                  }
                }}
              />
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-xs"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  addBulkTagDraft();
                }}
                disabled={!normalizeTagClient(bulkTagDraft)}
              >
                Add tag
              </Button>
            </div>
            <Button
              type="button"
              disabled={
                busy ||
                (!bulkTagSelected.length && !normalizeTagClient(bulkTagDraft))
              }
              onClick={handleBulkTagSave}
            >
              Apply tags
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!preview}
        onOpenChange={(o) => {
          if (!o) {
            setPreview(null);
            clearPreviewMedia();
          }
        }}
      >
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="truncate pr-8">
              {preview?.name || "Preview"}
            </DialogTitle>
          </DialogHeader>
          {previewSheets?.length > 1 && (
            <div className="flex flex-wrap gap-1 shrink-0">
              {previewSheets.map((sheet, idx) => (
                <Button
                  key={`${sheet.name}-${idx}`}
                  type="button"
                  size="sm"
                  variant={idx === previewSheetIndex ? "default" : "outline"}
                  onClick={() => setPreviewSheetIndex(idx)}
                >
                  {sheet.name}
                </Button>
              ))}
            </div>
          )}
          <div className="flex-1 min-h-0 overflow-auto rounded-md border border-border bg-muted/20 p-2">
            {previewText != null ? (
              previewIsMarkdown ? (
                <div className="p-3">
                  <MarkdownBody content={previewText} />
                </div>
              ) : (
                <pre className="text-xs whitespace-pre-wrap font-mono p-2">
                  {previewText}
                </pre>
              )
            ) : previewHtml != null ? (
              <div
                className="office-preview prose prose-sm dark:prose-invert max-w-none p-3 [&_table]:w-full [&_table]:border-collapse [&_td]:border [&_td]:border-border [&_td]:px-2 [&_td]:py-1 [&_th]:border [&_th]:border-border [&_th]:px-2 [&_th]:py-1 [&_th]:bg-muted/40"
                dangerouslySetInnerHTML={{ __html: previewHtml }}
              />
            ) : previewSheets != null ? (
              previewSheets.length === 0 ? (
                <p className="text-sm text-muted-foreground p-4">
                  This workbook has no sheets.
                </p>
              ) : (
                <div
                  className="office-preview prose prose-sm dark:prose-invert max-w-none p-3 overflow-x-auto [&_table]:w-full [&_table]:border-collapse [&_td]:border [&_td]:border-border [&_td]:px-2 [&_td]:py-1 [&_th]:border [&_th]:border-border [&_th]:px-2 [&_th]:py-1 [&_th]:bg-muted/40"
                  dangerouslySetInnerHTML={{
                    __html:
                      previewSheets[previewSheetIndex]?.html ||
                      previewSheets[0]?.html ||
                      "",
                  }}
                />
              )
            ) : previewUrl &&
              (preview?.mime_type || "").startsWith("image/") ? (
              <img
                src={previewUrl}
                alt={preview?.name}
                className="max-w-full max-h-[60vh] mx-auto object-contain"
              />
            ) : previewUrl && preview?.mime_type === "application/pdf" ? (
              <iframe
                title={preview?.name}
                src={previewUrl}
                className="w-full h-[60vh] rounded"
              />
            ) : (
              <p className="text-sm text-muted-foreground p-4">Loading…</p>
            )}
          </div>
          {preview && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="self-start"
              onClick={() => handleDownload(preview)}
            >
              <Download className="h-3.5 w-3.5 mr-1" />
              Download
            </Button>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamFilesPanel;
